import Box from '@mui/material/Box';
import { useCircularShape } from 'components/editor/SidePanel/content/DesignSettings/Textblock/useCircularShape';
import type { ReactNode } from 'react';
import { useRef, useLayoutEffect } from 'react';
import type { BlockContent } from 'components/editor/grid/reduxStore/editorSlice';
import type { BlockSettings } from 'components/editor/grid/reduxStore/blockStyleSettingsSlice';
import { useCircularTextEllipsis, usePasteHandler } from './useCircularTextHooks';

const generateCircle = ({ isLeft }: { isLeft: boolean }) => {
  const basePoints = [
    [2, 0],
    [100, 0],
    [100, 100],
    [2, 100],
    [50, 94],
    [76.6, 82.7],
    [94, 65.6],
    [100, 50],
    [94, 32.6],
    [76.6, 17.3],
    [50, 6],
  ];

  const points = isLeft ? basePoints.map(([x, y]) => [100 - x, y]) : basePoints;
  const polygonString = points.map(([x, y]) => `${x}% ${y}%`).join(', ');
  return `polygon(${polygonString})`;
};

const CIRCULAR_TEXT_STYLES = {
  height: '100%',
  width: '100%',
  textAlign: 'center',

  '.tiptap-textblock-wrapper': {
    height: '100%',
    width: '100%',
    overflow: 'hidden',
    position: 'relative',

    '&::before': {
      content: '""',
      height: '100%',
      width: '50%',
      float: 'left',
      shapeOutside: generateCircle({ isLeft: true }),
    },
    '&::after': {
      content: '""',
      position: 'absolute',
      bottom: 0,
      left: 0,
      right: 0,
      height: '30px',
      background: 'linear-gradient(transparent, white)',
      zIndex: 2,
      display: 'var(--show-ellipsis, none)',
    },
    '& > .tiptap': {
      width: '100%',
      height: '100%',
      wordWrap: 'normal',
      '::before': {
        content: '""',
        float: 'right',
        height: '100%',
        width: '50%',
        shapeOutside: generateCircle({ isLeft: false }),
      },
    },
  },
} as const;

type TextWrapContainerProps = {
  children: ReactNode;
  borderRadius: number;
  blockContent: BlockContent;
  borderWidth: Partial<BlockSettings>;
  handleOnClick?: () => void;
};

const TextWrapContainer = ({ children, handleOnClick, borderRadius, blockContent, borderWidth }: TextWrapContainerProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const { wouldBeCircular } = useCircularShape(blockContent);
  const shouldApplyCircularWrapping = wouldBeCircular(borderRadius, borderWidth);

  const { updateEllipsisAndResetScroll } = useCircularTextEllipsis(containerRef);
  usePasteHandler(containerRef, shouldApplyCircularWrapping, updateEllipsisAndResetScroll);

  useLayoutEffect(() => {
    updateEllipsisAndResetScroll();
  }, [blockContent, borderRadius, updateEllipsisAndResetScroll]);

  return (
    <Box
      data-testid="text-wrap-container"
      sx={shouldApplyCircularWrapping ? CIRCULAR_TEXT_STYLES : {}}
      width="100%"
      height="100%"
      ref={containerRef}
      onClick={() => (handleOnClick ? handleOnClick() : null)}
    >
      {children}
    </Box>
  );
};

export default TextWrapContainer;
